import type { ChangeEvent } from 'react'
import {
  Avatar,
  Button,
  EditIcon,
  ErrorMessage,
  Field,
  FormLabel,
  Input,
  MultiSelect,
  Paragraph,
  Select,
  Switch,
} from '@quickmd-llc/design-system'
import { EditAvatarUpload } from '@/components/edit-member-image/EditAvatarUpload'
import { formatPhoneNumber } from '@quickmd-llc/frontend-utils'
import { medicalProfessionOptions, prefixOptions, suffixOptions } from '@/utils/commonMembersPage'
import type { DoctorAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

/** Redux member includes extra fields not in the API response type */
type MemberData = DoctorAdminApiOutContract & { UserType?: string | null }

interface GeneralTabProps {
  member: MemberData
  isEditMode: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  onSetFormValues: (field: string, value: unknown) => void
  profileImgInputValue: File | string | null | undefined
  isImageChanged: boolean
  setProfileImgInputValue: (value: File | string) => void
  setIsImageChanged: (value: boolean) => void
  firstName: string
  lastName: string
  prefix: string
  suffix: string
  setPrefix: (value: string) => void
  setSuffix: (value: string) => void
  email: string
  phoneNumber: string
  professionalTitle: string
  npi: string
  onPhoneBlur: () => void
  isExcludedFromMarketingReports: boolean
  onExcludeFromReportingChange: (value: boolean) => void
  onAccountActiveChange: () => void
  emailError?: string
  phoneError?: string
  professionalTitleError?: string
  firstNameError?: string
  lastNameError?: string
}

function formatPhone(phoneObj: { Number?: string | null } | null | undefined): string {
  if (!phoneObj?.Number) return '--'
  return formatPhoneNumber(phoneObj.Number)
}

export function GeneralTab({
  member,
  isEditMode,
  onEdit,
  onCancel,
  onSave,
  onSetFormValues,
  profileImgInputValue,
  isImageChanged,
  setProfileImgInputValue,
  setIsImageChanged,
  firstName,
  lastName,
  prefix,
  suffix,
  setPrefix,
  setSuffix,
  email,
  phoneNumber,
  professionalTitle,
  npi,
  onPhoneBlur,
  isExcludedFromMarketingReports,
  onExcludeFromReportingChange,
  onAccountActiveChange,
  emailError,
  phoneError,
  professionalTitleError,
  firstNameError,
  lastNameError,
}: GeneralTabProps) {
  const isActive = member?.IsVerified ? (member.IsActive ?? false) : null
  const role = member?.UserType ?? ''
  const isProvider = role === 'Provider'
  const phoneObj = member?.PhoneNumber ?? null
  const npiDisplay = member?.NationalProviderIdentifier || '--'
  const profileImgSrc = typeof profileImgInputValue === 'string' ? profileImgInputValue : undefined
  const fullName = `${member?.FirstName ?? ''} ${member?.LastName ?? ''}`.trim()

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col'>
      <div className='w-full rounded-lg border border-border-dark bg-white p-sp-6'>
        <div className='flex items-start gap-sp-3'>
          {/* Profile photo */}
          {isEditMode ?
            <EditAvatarUpload
              fullName={fullName}
              profileImgInputValue={profileImgInputValue}
              isImageChanged={isImageChanged}
              setProfileImgInputValue={setProfileImgInputValue}
              setIsImageChanged={setIsImageChanged}
            />
          : <Avatar name={fullName} size='xxl' {...(profileImgSrc ? { src: profileImgSrc } : {})} />}

          {/* Fields */}
          <div className='flex flex-1 flex-col gap-sp-3'>
            {/* Edit-only name fields: Prefix + First name; Last name + Suffix */}
            {isEditMode ?
              <>
                <div className='grid grid-cols-2 gap-sp-3'>
                  <Field>
                    <FormLabel>Prefix</FormLabel>
                    <Select value={prefix} onChange={(e: ChangeEvent<HTMLSelectElement>) => setPrefix(e.target.value)}>
                      {prefixOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.trim() || 'None'}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field error={!!firstNameError}>
                    <FormLabel>First name</FormLabel>
                    {firstNameError ?
                      <ErrorMessage>{firstNameError}</ErrorMessage>
                    : null}
                    <Input
                      value={firstName ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => onSetFormValues('firstName', e.target.value)}
                      error={!!firstNameError}
                    />
                  </Field>
                </div>
                <div className='grid grid-cols-2 gap-sp-3'>
                  <Field error={!!lastNameError}>
                    <FormLabel>Last name</FormLabel>
                    {lastNameError ?
                      <ErrorMessage>{lastNameError}</ErrorMessage>
                    : null}
                    <Input
                      value={lastName ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => onSetFormValues('lastName', e.target.value)}
                      error={!!lastNameError}
                    />
                  </Field>
                  <Field>
                    <FormLabel>Suffix</FormLabel>
                    <Select value={suffix} onChange={(e: ChangeEvent<HTMLSelectElement>) => setSuffix(e.target.value)}>
                      {suffixOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.trim() || 'None'}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </>
            : null}

            {/* Row 1: Email + Role */}
            <div className='grid grid-cols-2 gap-sp-3'>
              <Field data-testid='pw_member-email-field' error={!!emailError}>
                <FormLabel>Email</FormLabel>
                {emailError ?
                  <ErrorMessage>{emailError}</ErrorMessage>
                : null}
                {isEditMode ?
                  <Input value={email} disabled error={!!emailError} />
                : <Paragraph size='md'>{email}</Paragraph>}
              </Field>
              <Field>
                <FormLabel>Role</FormLabel>
                {isEditMode ?
                  <Input value={role} disabled />
                : <Paragraph size='md'>{role}</Paragraph>}
              </Field>
            </div>

            {/* Row 2: Professional title + Phone */}
            {isProvider ?
              <div className='grid grid-cols-2 gap-sp-3'>
                <Field error={!!professionalTitleError}>
                  <FormLabel>Professional title</FormLabel>
                  {professionalTitleError ?
                    <ErrorMessage>{professionalTitleError}</ErrorMessage>
                  : null}
                  {isEditMode ?
                    <MultiSelect
                      value={
                        professionalTitle ?
                          professionalTitle
                            .split(';')
                            .map((t) => t.trim())
                            .filter(Boolean)
                        : []
                      }
                      onChange={(values) => onSetFormValues('ProfessionalTitle', values.join(';'))}
                      options={medicalProfessionOptions}
                      placeholder='Select title'
                      error={!!professionalTitleError}
                    />
                  : <Paragraph size='md'>{member?.ProfessionalTitles?.join('; ') || '--'}</Paragraph>}
                </Field>
                <Field error={!!phoneError}>
                  <FormLabel>Phone</FormLabel>
                  {phoneError ?
                    <ErrorMessage>{phoneError}</ErrorMessage>
                  : null}
                  {isEditMode ?
                    <Input
                      type='tel'
                      inputMode='numeric'
                      value={phoneNumber}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onSetFormValues('phoneNumber', e.target.value.replace(/\D/g, ''))
                      }
                      onBlur={onPhoneBlur}
                      placeholder='5551234567'
                      error={!!phoneError}
                    />
                  : <Paragraph size='md'>{formatPhone(phoneObj)}</Paragraph>}
                </Field>
              </div>
            : null}

            {/* Row 3: NPI + Exclude from reporting */}
            {isProvider ?
              <div className='grid grid-cols-2 gap-sp-3'>
                <Field>
                  <FormLabel>NPI</FormLabel>
                  {isEditMode ?
                    <Input
                      inputMode='numeric'
                      value={npi}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => onSetFormValues('NPI', e.target.value)}
                      placeholder='0000000000'
                    />
                  : <Paragraph size='md'>{npiDisplay}</Paragraph>}
                </Field>
                <Field>
                  <FormLabel>Exclude from reporting</FormLabel>
                  {isEditMode ?
                    <div className='mt-sp-2 flex items-center gap-sp-1'>
                      <Switch
                        size='s'
                        checked={isExcludedFromMarketingReports}
                        onChange={() => onExcludeFromReportingChange(!isExcludedFromMarketingReports)}
                      />
                      <span className='text-sm font-semibold tracking-[0.12px] text-text-dark'>
                        {isExcludedFromMarketingReports ? 'Yes' : 'No'}
                      </span>
                    </div>
                  : <Paragraph size='md'>{isExcludedFromMarketingReports ? 'Yes' : 'No'}</Paragraph>}
                </Field>
              </div>
            : null}

            {/* Row 4: Active account */}
            {isActive !== null && (
              <Field>
                <FormLabel>Active account</FormLabel>
                {/* 16px gap between label and switch (per Figma). DS Field only auto-spaces elements
                  with data-slot=control; this raw div needs an explicit mt. */}
                <div className='mt-sp-2 flex items-center gap-sp-1'>
                  <Switch size='s' checked={isActive} onChange={onAccountActiveChange} />
                  <span className='text-sm font-semibold tracking-[0.12px] text-text-dark'>
                    {isActive ? 'Yes' : 'No'}
                  </span>
                </div>
              </Field>
            )}
          </div>

          {/* Edit button — view mode only */}
          {!isEditMode && (
            <Button data-testid='pw_member-edit-btn' priority='tertiary' onClick={onEdit} className='shrink-0'>
              <EditIcon iconSize='s' />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Edit mode footer — pinned to page bottom, breaks out of container padding */}
      {isEditMode ?
        <div
          data-testid='pw_member-action-bar'
          className='sticky bottom-0 -mx-sp-6 -mb-sp-6 mt-auto flex justify-end gap-sp-2 border-t border-border-dark bg-white px-sp-6 py-sp-3'>
          <Button priority='tertiary' onClick={onCancel}>
            Cancel
          </Button>
          <Button data-testid='pw_member-save-btn' priority='primary' onClick={onSave}>
            Save changes
          </Button>
        </div>
      : null}
    </div>
  )
}
